import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
    Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, Chip, Skeleton, Container, Paper, Button, Grid, TextField, IconButton, Tooltip,
} from '@mui/material';
import { ArrowBack, PlayArrow, Search } from '@mui/icons-material';
import NavBar from '../../../Components/NavBar/navbar';
import ArcadeHubNav, { arcadeHubPath, ARCADE_SECTIONS } from '../ArcadeHubNav';
import { API_ENDPOINTS } from '../../../utils/config';
import { THEME } from '../../../theme';
import { buildGameNameMap, gameDisplayName, resolveUserLabel, formatAgentLabel, formatRunSource, isHumanRunSource } from './displayHelpers';
import { EmptyState } from '../../../Components/Arcade/ui/StateViews';
import usePageTitle from '../../../Components/Arcade/ui/usePageTitle';

function StatBox({ label, value }) {
    return (
        <Box sx={{ p: 1.5, bgcolor: 'rgba(255,255,255,0.03)', borderRadius: 2 }}>
            <Typography variant="caption" sx={{ color: THEME.secondary }}>{label}</Typography>
            <Typography variant="h6" sx={{ color: 'white' }}>{value}</Typography>
        </Box>
    );
}

function SubmissionsTable({ rows, loading, gameNameMap = {} }) {
    if (loading) {
        return (
            <Box sx={{ p: 2 }}>
                {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} variant="text" height={36} sx={{ bgcolor: 'rgba(255,255,255,0.08)' }} />
                ))}
            </Box>
        );
    }
    if (!rows.length) {
        return <EmptyState message="No submissions yet." />;
    }
    return (
        <TableContainer sx={{ maxHeight: 480 }}>
            <Table stickyHeader size="small">
                <TableHead>
                    <TableRow>
                        {['Game', 'Agent', 'Score', 'Source', 'Date', 'Replay'].map((h) => (
                            <TableCell key={h} sx={{ bgcolor: THEME.cardBg, color: THEME.secondary, borderBottom: `1px solid ${THEME.border}` }}>{h}</TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row, idx) => {
                        const runId = row.run_id || row._id || row.id;
                        const gameId = row.game_id || row.game;
                        return (
                            <TableRow key={runId || idx} hover>
                                <TableCell sx={{ color: 'white', borderBottom: `1px solid ${THEME.grid}` }}>
                                    <Link to={`/arcade/dashboard/games/${gameId}`} style={{ color: 'white', textDecoration: 'none' }}>
                                        {gameDisplayName(gameId, gameNameMap)}
                                    </Link>
                                </TableCell>
                                <TableCell sx={{ color: THEME.secondary, borderBottom: `1px solid ${THEME.grid}` }}>
                                    {row.agent_id && !isHumanRunSource(row.source) ? (
                                        <Link to={`/arcade/dashboard/agents/${row.agent_id}`} style={{ color: THEME.secondary, textDecoration: 'none' }}>
                                            {formatAgentLabel(row)}
                                        </Link>
                                    ) : formatAgentLabel(row)}
                                </TableCell>
                                <TableCell sx={{ borderBottom: `1px solid ${THEME.grid}` }}>
                                    <Chip size="small" label={row.score ?? 0} sx={{ bgcolor: 'transparent', border: `1px solid ${THEME.primary}`, color: THEME.primary, fontWeight: 'bold' }} />
                                </TableCell>
                                <TableCell sx={{ color: THEME.secondary, borderBottom: `1px solid ${THEME.grid}` }}>{formatRunSource(row.source)}</TableCell>
                                <TableCell sx={{ color: THEME.secondary, borderBottom: `1px solid ${THEME.grid}` }}>
                                    {row.timestamp ? new Date(row.timestamp).toLocaleDateString() : '—'}
                                </TableCell>
                                <TableCell sx={{ borderBottom: `1px solid ${THEME.grid}` }}>
                                    {runId ? (
                                        <Tooltip title="Play">
                                            <IconButton
                                                size="small"
                                                component={Link}
                                                to={`/arcade/replay/${runId}`}
                                                aria-label="Play"
                                                sx={{ color: THEME.primary }}
                                            >
                                                <PlayArrow fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    ) : '—'}
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

/**
 * Dual-purpose: embedded "Users" tab in DashboardHome (props: leaderboard, loading)
 * or standalone routed page at /arcade/dashboard/users/:userId.
 */
const UserProfileView = ({ games = [], leaderboard = [], loading: loadingProp = false }) => {
    const { userId: routeUserId } = useParams();
    usePageTitle(routeUserId ? `Player ${routeUserId}` : 'Dashboard');
    const isStandalone = Boolean(routeUserId);

    const [search, setSearch] = useState('');
    const [stats, setStats] = useState(null);
    const [submissions, setSubmissions] = useState([]);
    const [loading, setLoading] = useState(loadingProp);
    const [error, setError] = useState(null);
    const [standaloneGames, setStandaloneGames] = useState([]);

    const gameNameMap = useMemo(
        () => buildGameNameMap(games.length ? games : standaloneGames),
        [games, standaloneGames]
    );

    useEffect(() => {
        if (!isStandalone) return;
        let mounted = true;
        async function fetchUserData() {
            setLoading(true);
            setError(null);
            try {
                const [statsRes, subsRes, gamesRes] = await Promise.all([
                    fetch(API_ENDPOINTS.USER_STATS(routeUserId)),
                    fetch(API_ENDPOINTS.USER_SUBMISSIONS(routeUserId)),
                    fetch(API_ENDPOINTS.GAMES_LIST),
                ]);
                if (statsRes.ok) {
                    const s = await statsRes.json();
                    if (mounted) setStats(s);
                }
                if (subsRes.ok) {
                    const s = await subsRes.json();
                    const list = s.submissions || (Array.isArray(s) ? s : []);
                    if (mounted) setSubmissions(list);
                } else if (mounted) {
                    setSubmissions([]);
                }
                if (gamesRes.ok) {
                    const g = await gamesRes.json();
                    if (mounted) setStandaloneGames(Array.isArray(g) ? g : (g.games || []));
                }
                if (!statsRes.ok && !subsRes.ok) {
                    throw new Error(`status ${statsRes.status}/${subsRes.status}`);
                }
            } catch (e) {
                if (mounted) setError('Failed to load user');
            }
            if (mounted) setLoading(false);
        }
        fetchUserData();
        return () => { mounted = false; };
    }, [isStandalone, routeUserId]);

    // Embedded aggregate view: distinct users derived client-side from leaderboard
    const userRows = useMemo(() => {
        const byUser = {};
        (leaderboard || []).forEach((e) => {
            const key = e.user_id || e.username || e.player_id;
            if (!key) return;
            const label = resolveUserLabel(e);
            if (!byUser[key]) {
                byUser[key] = {
                    user_id: e.user_id,
                    username: label,
                    runs: 0,
                    bestScore: -Infinity,
                };
            } else if (byUser[key].username === '—' || /^[0-9a-fA-F]{8}…$/.test(byUser[key].username)) {
                byUser[key].username = label;
            }
            byUser[key].runs += 1;
            const score = Number(e.score) || 0;
            if (score > byUser[key].bestScore) byUser[key].bestScore = score;
        });
        return Object.values(byUser).filter((u) =>
            !search || u.username?.toLowerCase().includes(search.toLowerCase())
        );
    }, [leaderboard, search]);

    if (!isStandalone) {
        return (
            <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                    <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 700 }}>Users</Typography>
                    <TextField
                        size="small"
                        placeholder="Search users..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        InputProps={{ startAdornment: <Search fontSize="small" sx={{ color: THEME.secondary, mr: 1 }} /> }}
                        sx={{ '& .MuiOutlinedInput-root': { color: 'white' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: THEME.border } }}
                    />
                </Box>
                {loadingProp ? (
                    <Box sx={{ p: 2 }}>
                        {[...Array(5)].map((_, i) => (
                            <Skeleton key={i} variant="text" height={36} sx={{ bgcolor: 'rgba(255,255,255,0.08)' }} />
                        ))}
                    </Box>
                ) : userRows.length === 0 ? (
                    <EmptyState message="No users found yet." />
                ) : (
                    <TableContainer sx={{ maxHeight: 480 }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    {['User', 'Runs', 'Best Score', ''].map((h) => (
                                        <TableCell key={h} sx={{ bgcolor: THEME.cardBg, color: THEME.secondary, borderBottom: `1px solid ${THEME.border}` }}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {userRows.map((u) => (
                                    <TableRow key={u.user_id || u.username} hover>
                                        <TableCell sx={{ color: 'white', borderBottom: `1px solid ${THEME.grid}` }}>{u.username}</TableCell>
                                        <TableCell sx={{ color: THEME.secondary, borderBottom: `1px solid ${THEME.grid}` }}>{u.runs}</TableCell>
                                        <TableCell sx={{ borderBottom: `1px solid ${THEME.grid}` }}>
                                            <Chip size="small" label={u.bestScore === -Infinity ? 0 : u.bestScore} sx={{ bgcolor: 'transparent', border: `1px solid ${THEME.primary}`, color: THEME.primary, fontWeight: 'bold' }} />
                                        </TableCell>
                                        <TableCell sx={{ borderBottom: `1px solid ${THEME.grid}` }}>
                                            {u.user_id && (
                                                <Button size="small" component={Link} to={`/arcade/dashboard/users/${u.user_id}`} sx={{ color: THEME.primary, textTransform: 'none' }}>
                                                    View Profile
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Box>
        );
    }

    return (
        <Box sx={{ bgcolor: THEME.bg, minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
            <NavBar />
            <Box sx={{ flexGrow: 1, pt: 12, pb: 8 }}>
                <Container maxWidth="lg">
                    <ArcadeHubNav activeSection={ARCADE_SECTIONS.dashboard} />
                    <Button component={Link} to={arcadeHubPath(ARCADE_SECTIONS.dashboard)} startIcon={<ArrowBack />} sx={{ color: THEME.secondary, mb: 3 }}>
                        Back to Dashboard
                    </Button>
                    <Typography variant="h3" sx={{ color: 'white', fontWeight: 800, mb: 1 }}>
                        {resolveUserLabel({ username: stats?.username, email: stats?.email, user_id: routeUserId })}
                    </Typography>
                    <Typography variant="body2" sx={{ color: THEME.secondary, mb: 3 }}>User Profile</Typography>

                    {error && (
                        <Paper sx={{ p: 2, mb: 3, bgcolor: 'rgba(239, 71, 67, 0.08)', border: `1px solid ${THEME.accent}`, borderRadius: 2 }}>
                            <Typography variant="body2" sx={{ color: THEME.accent }}>
                                Some profile data failed to load ({error}).
                            </Typography>
                        </Paper>
                    )}

                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={6} sm={3}>
                            <StatBox label="Games Solved" value={loading ? '…' : (stats?.solved_count ?? stats?.games_solved ?? '—')} />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <StatBox label="Best Score" value={loading ? '…' : (stats?.best_score ?? '—')} />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <StatBox label="Total Submissions" value={loading ? '…' : (stats?.total_submissions ?? submissions.length)} />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <StatBox label="Recent Runs" value={loading ? '…' : (stats?.recent_runs?.length ?? '—')} />
                        </Grid>
                    </Grid>

                    <Paper sx={{ bgcolor: THEME.cardBg, border: `1px solid ${THEME.border}`, borderRadius: 3 }}>
                        <Box sx={{ p: 2, borderBottom: `1px solid ${THEME.border}` }}>
                            <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>Submissions</Typography>
                        </Box>
                        <SubmissionsTable rows={submissions} loading={loading} gameNameMap={gameNameMap} />
                    </Paper>
                </Container>
            </Box>
        </Box>
    );
};

export default UserProfileView;
